<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/vue-db'
import { ComboboxGroup, ComboboxItem, ComboboxLabel } from 'reka-ui'
import { computed } from 'vue'

import { constructAddress, issuesCollection, issueTypesCollection } from '@/collections'
import { route } from '@/Composables/route'
import { prefetchVisit, type PaletteCommand } from '@/Composables/useCommandPalette'
import { getStatusColor, getStatusLabel } from '@/lib/issue'

const props = defineProps<{
  query: string
}>()

const RESULT_LIMIT = 7

interface IssueSearchEntry {
  id: number
  status: App.Enums.IssueStatus
  title: string
  address: string | null
  haystack: string
}

const { data: issueRows } = useLiveQuery((query) =>
  query
    .from({ issue: issuesCollection })
    .leftJoin({ issueType: issueTypesCollection }, ({ issue, issueType }) =>
      eq(issueType.id, issue.issue_type_id),
    )
    .orderBy(({ issue }) => issue.created_at, 'desc')
    .select(({ issue, issueType }) => ({
      id: issue.id,
      status: issue.status,
      description: issue.description,
      street: issue.street,
      house_number: issue.house_number,
      postal_code: issue.postal_code,
      city: issue.city,
      typeName: issueType.name,
    })),
)

// The haystack doubles as the item's text-value: reka-ui re-filters rendered items by
// substring against it, so the matching below must stay a substring match over the same string.
const searchIndex = computed<IssueSearchEntry[]>(() =>
  issueRows.value.map((row) => {
    const title = [row.typeName, row.description].filter(Boolean).join(': ') || 'Meldung'
    const address = constructAddress(row.street, row.house_number, row.postal_code, row.city)

    return {
      id: row.id,
      status: row.status,
      title,
      address,
      haystack:
        `#${row.id} ${title} ${address ?? ''} ${getStatusLabel(row.status)}`.toLocaleLowerCase(
          'de',
        ),
    }
  }),
)

const matches = computed<IssueSearchEntry[]>(() => {
  const query = props.query.trim().toLocaleLowerCase('de')
  if (query.length === 0) {
    return []
  }

  const issueNumber = /^#?(\d+)$/.exec(query)?.[1]
  if (issueNumber !== undefined) {
    return searchIndex.value
      .filter((entry) => String(entry.id).startsWith(issueNumber))
      .sort((a, b) => Number(String(b.id) === issueNumber) - Number(String(a.id) === issueNumber))
      .slice(0, RESULT_LIMIT)
  }

  return searchIndex.value.filter((entry) => entry.haystack.includes(query)).slice(0, RESULT_LIMIT)
})

function toCommand(entry: IssueSearchEntry): PaletteCommand {
  const url = route('admin.issues.show', { issue: entry.id })

  return {
    id: `issue:${entry.id}`,
    title: entry.title,
    perform: () => router.visit(url),
    onHighlight: prefetchVisit(url),
  }
}
</script>

<template>
  <ComboboxGroup v-if="matches.length > 0">
    <ComboboxLabel
      class="px-3 pt-3 pb-1.5 text-xs font-medium text-zinc-400 first:pt-1 dark:text-zinc-500"
    >
      Meldungen
    </ComboboxLabel>

    <ComboboxItem
      v-for="entry in matches"
      :key="entry.id"
      :value="toCommand(entry)"
      :text-value="entry.haystack"
      class="group flex cursor-default items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-zinc-700 outline-none data-highlighted:bg-zinc-100 data-highlighted:text-zinc-950 dark:text-zinc-300 dark:data-highlighted:bg-zinc-800 dark:data-highlighted:text-white"
    >
      <span
        class="size-2 shrink-0 rounded-full"
        :style="{ backgroundColor: getStatusColor(entry.status).fill }"
        :title="getStatusLabel(entry.status)"
        aria-hidden="true"
      />
      <span class="shrink-0 text-zinc-400 tabular-nums group-data-highlighted:text-zinc-500">
        #{{ entry.id }}
      </span>
      <span class="min-w-0 flex-1 truncate">{{ entry.title }}</span>
      <span v-if="entry.address" class="max-w-44 shrink-0 truncate text-xs text-zinc-400">
        {{ entry.address }}
      </span>
    </ComboboxItem>
  </ComboboxGroup>
</template>
